import { Injectable } from '@angular/core';
import { MatSnackBar, MatSnackBarConfig } from '@angular/material/snack-bar';

export type ToastType = 'success' | 'error' | 'info';

@Injectable({
  providedIn: 'root'
})
export class ToastService {
  constructor(private snackBar: MatSnackBar) {}

  /**
   * Show a success toast
   */
  success(message: string, duration: number = 5000): void {
    this.show(message, 'success', duration);
  }

  /**
   * Show an error toast
   */
  error(message: string, duration: number = 5000): void {
    this.show(message, 'error', duration);
  }

  /**
   * Show an info toast
   */
  info(message: string, duration: number = 5000): void {
    this.show(message, 'info', duration);
  }

  /**
   * Show a toast with custom configuration
   */
  private show(message: string, type: ToastType, duration: number): void {
    const config: MatSnackBarConfig = {
      duration,
      horizontalPosition: 'end',
      verticalPosition: 'top',
      panelClass: [`toast-${type}`]
    };

    // The icon will be displayed via CSS ::before pseudo-element
    this.snackBar.open(message, '', config);

    // Note: Positioning is now handled by CSS in toast-overrides.scss
    // No need for DOM manipulation which caused visual glitches
  }

  /**
   * Force toast to bottom-right on mobile by directly manipulating DOM
   */
  private forceRightPositioning(): void {
    // Use multiple RAF calls to ensure styles are applied after Material's positioning
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const overlayContainer = document.querySelector('.cdk-overlay-container');
        if (!overlayContainer) return;

        // Find the overlay wrapper containing snackbar
        const wrappers = overlayContainer.querySelectorAll('.cdk-global-overlay-wrapper');
        wrappers.forEach(wrapper => {
          const snackbar = wrapper.querySelector('.mat-mdc-snack-bar-container');
          if (snackbar) {
            const wrapperEl = wrapper as HTMLElement;
            const pane = wrapper.querySelector('.cdk-overlay-pane') as HTMLElement;

            // Force right alignment via inline styles (highest specificity)
            wrapperEl.style.setProperty('justify-content', 'flex-end', 'important');
            wrapperEl.style.setProperty('align-items', 'flex-end', 'important');

            if (pane) {
              pane.style.setProperty('left', 'auto', 'important');
              pane.style.setProperty('right', '0', 'important');
              pane.style.setProperty('bottom', '0', 'important');
            }

            const snackbarEl = snackbar as HTMLElement;
            snackbarEl.style.setProperty('left', 'auto', 'important');
            snackbarEl.style.setProperty('right', '0', 'important');
            snackbarEl.style.setProperty('margin-right', '16px', 'important');
            snackbarEl.style.setProperty('margin-left', 'auto', 'important');
          }
        });
      });
    });
  }

  /**
   * Dismiss all toasts
   */
  dismiss(): void {
    this.snackBar.dismiss();
  }
}
